<template>
    <Dialog :visible="visible" modal dismissableMask :closable="false" :draggable="false" :style="{ width: '36rem' }"
        contentClass="!p-0 overflow-hidden rounded-lg" :pt="{ header: { class: '!p-0' } }"
        @update:visible="v => !v && emit('close')">
        <!-- HEADER -->
        <template #header>
            <div class="flex items-center gap-4 w-full p-3 rounded-t-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                <div class="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <i class="fa-regular fa-pen-to-square text-white text-xl" />
                </div>

                <div class="flex-1 min-w-0">
                    <h4 class="text-xl font-bold">
                        {{ t('users.editUser') }}
                    </h4>
                </div>

                <Button icon="fa-solid fa-xmark" rounded text severity="secondary" :aria-label="t('common.close')"
                    @click="emit('close')" />
            </div>
        </template>

        <!-- BODY -->
        <div class="flex flex-col h-full">
            <!-- CONTENT -->
            <div class="flex-1 overflow-y-auto p-4">
                <div class="space-y-6">
                    <!-- USERNAME -->
                    <div>
                        <label class="font-semibold mb-2 block">
                            {{ t('users.username') }}
                        </label>
                        <InputText :modelValue="localUser.username" disabled class="w-full" />
                    </div>

                    <!-- EMAIL -->
                    <div>
                        <label class="font-semibold mb-2 block">
                            {{ t('users.email') }}
                        </label>
                        <InputText :modelValue="localUser.email" disabled class="w-full" />
                    </div>

                    <!-- FIRST NAME -->
                    <div>
                        <label class="font-semibold mb-2 block">
                            {{ t('common.name') }}
                        </label>
                        <InputText :modelValue="localUser.firstName" disabled class="w-full" />
                    </div>

                    <!-- LAST NAME -->
                    <div>
                        <label class="font-semibold mb-2 block">
                            {{ t('users.lastName') }}
                        </label>
                        <InputText :modelValue="localUser.lastName" disabled class="w-full" />
                    </div>

                    <!-- ROLE -->
                    <div>
                        <label class="font-semibold mb-2 block">
                            {{ t('users.role') }} <span class="text-red-500">*</span>
                        </label>
                        <Select v-model="localUser.role" :options="availableRoles" optionLabel="name" required
                            class="w-full" :placeholder="t('users.selectRole')">
                            <template #option="{ option }">
                                <div>
                                    <div class="font-semibold">{{ option.name }}</div>
                                    <div v-if="option.description" class="">
                                        {{ option.description }}
                                    </div>
                                </div>
                            </template>
                        </Select>
                    </div>

                    <!-- TYPE -->
                    <div>
                        <label class="font-semibold mb-2 block">
                            {{ t('users.type') }} <span class="text-red-500">*</span>
                        </label>
                        <Select v-model="localUser.type" :options="userTypes" optionLabel="label" optionValue="value"
                            required class="w-full" :placeholder="t('users.selectType')">
                            <template #option="{ option }">
                                <div class="flex items-center gap-2">
                                    <i :class="[option.icon]" />
                                    <span>{{ option.label }}</span>
                                </div>
                            </template>
                            <template #value="{ value }">
                                <div v-if="value !== null && value !== undefined" class="flex items-center gap-2">
                                    <i :class="[userTypes.find(t => t.value === value)?.icon]" />
                                    <span>{{userTypes.find(t => t.value === value)?.label}}</span>
                                </div>
                            </template>
                        </Select>
                    </div>

                    <!-- STATUS -->
                    <div>
                        <label class="font-semibold mb-2 block">
                            {{ t('common.status') }} <span class="text-red-500">*</span>
                        </label>
                        <Select v-model="localUser.enabled" :options="[
                            { label: t('common.enabled'), value: true, icon: 'fa-solid fa-check-circle', severity: 'success' },
                            { label: t('common.disabled'), value: false, icon: 'fa-solid fa-ban', severity: 'danger' }
                        ]" optionLabel="label" optionValue="value" required class="w-full">
                            <template #option="{ option }">
                                <div class="flex items-center gap-2">
                                    <i
                                        :class="[option.icon, option.severity === 'success' ? 'text-green-500' : 'text-red-500']" />
                                    <span>{{ option.label }}</span>
                                </div>
                            </template>
                            <template #value="{ value }">
                                <div v-if="value !== null && value !== undefined" class="flex items-center gap-2">
                                    <i
                                        :class="[value ? 'fa-solid fa-check-circle text-green-500' : 'fa-solid fa-ban text-red-500']" />
                                    <span>{{ value ? t('common.enabled') : t('common.disabled') }}</span>
                                </div>
                            </template>
                        </Select>
                    </div>
                </div>
            </div>

            <!-- FOOTER -->
            <div class="flex justify-end p-3 border-t bg-gray-50">
                <Button type="button" :label="t('common.save')" severity="success" @click="save" />
            </div>
        </div>
    </Dialog>
</template>

<script setup lang="ts">
import { User, UserType } from '@/lib/types/user';
import { getErrorMessage, getUserTypeLabel } from '@/plugins/i18n/enums';
import { useUsersStore } from '@/store/user';
import { useToast } from 'primevue/usetoast';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const store = useUsersStore();
const toast = useToast();

const props = defineProps<{
    visible: boolean
    mode: 'create' | 'edit' | 'delete'
    user: User | null
}>();

const emit = defineEmits(['close', 'saved']);

/* --------------------
   STATE
-------------------- */

function emptyUser(): User {
    const defaultRole = store.roles?.[0] ?? {
        id: '',
        code: '',
        name: '',
        description: null,
        enabled: true
    };

    return {
        id: '',
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        role: defaultRole,
        type: UserType.User,
        permissions: [],
        enabled: true
    };
}

const localUser = ref<User>(emptyUser());

/* --------------------
   COMPUTED
-------------------- */

const availableRoles = computed(() => store.roles || []);

const userTypes = computed(() => [
    { label: t(getUserTypeLabel(UserType.User)), value: UserType.User, icon: 'fa-solid fa-user' },
    { label: t(getUserTypeLabel(UserType.Admin)), value: UserType.Admin, icon: 'fa-solid fa-user-tie' },
    { label: t(getUserTypeLabel(UserType.SuperAdmin)), value: UserType.SuperAdmin, icon: 'fa-solid fa-user-shield' }
]);

/* --------------------
   WATCH
-------------------- */

watch(
    () => [props.visible, props.user],
    ([visible, user]) => {
        if (!visible) return;

        if (user) {
            localUser.value = Object.assign(emptyUser(), user);
        } else {
            localUser.value = emptyUser();
        }
    },
    { immediate: true }
)

/* --------------------
   ACTIONS
-------------------- */

async function save() {
    try {
        const error = await store.updateOne(localUser.value);

        if (error) {
            toast.add({
                severity: 'error',
                summary: t('error.title'),
                detail: getErrorMessage(error, t),
                life: 5000
            });
            return;
        }

        emit('saved');
        emit('close');

    } catch (err: any) {
        toast.add({
            severity: 'error',
            summary: t('error.title'),
            detail: err.message || t('error.updateUser'),
            life: 5000
        });
    }
}
</script>
